import curry from "../../../lib/core/functional/curry";

const voidPath = "M0 0h24v24H0z";
const hidePath = "M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z";
const showPath = "M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z";
const replyPath = "M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z";
const sharePath =
    "M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z";
const likePath =
    "M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z";
const dislikePath =
    "M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z";
const favoritePath =
    "M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z";
const approvePath =
    "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z";
const cancelPath =
    "M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z";
const featuredPath =
    "M23 12l-2.44-2.78.34-3.68-3.61-.82-1.89-3.18L12 3 8.6 1.54 6.71 4.72l-3.61.81.34 3.68L1 12l2.44 2.78-.34 3.69 3.61.82 1.89 3.18L12 21l3.4 1.46 1.89-3.18 3.61-.82-.34-3.68L23 12zm-10 5h-2v-2h2v2zm0-4h-2V7h2v6z";
const morePath =
    "M6 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm12 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm-6 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z";
const dissatisfiedPath =
    "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm0-6c-2.33 0-4.32 1.45-5.12 3.5h1.67c.69-1.19 1.97-2 3.45-2s2.75.81 3.45 2h1.67c-.8-2.05-2.79-3.5-5.12-3.5z";

const defaultActiveLayouts = [
    "avatar",
    "update_avatar",
    "title",
    "bio",
    "follow",
    "date",
    "review",
    "update_author_rating",
    "message",
];

const renderModule = curry(function renderCommentModule(
    { h, hooks, Fragment },
    _context,
    { formatDate, layoutOrder, layoutStatus, copyToClipboard, CommentBox, Review },
    _altLoader
) {
    const { useState, useCallback, useEffect } = hooks;

    function isLayoutActive(layoutName) {
        if (layoutStatus && layoutName in layoutStatus) {
            return layoutStatus[layoutName] !== "inactive";
        }
        return defaultActiveLayouts.includes(layoutName);
    }

    const formatLong = (ts) => formatDate(new Date(parseInt(ts) * 1000), "MMMM Do yyyy, h:mm:ss a");
    const formatShort = (ts) => formatDate(new Date(parseInt(ts) * 1000), "d MMM, yyyy");

    const VoidPath = <path d={voidPath} fill="none"></path>;

    const FeaturedRibbon = (
        <a href="javascript:;" class="material-icons status-featured selected" title="Featured">
            <svg xmlns="http://www.w3.org/2000/svg" height="17px" viewBox="0 0 24 24" width="17px">
                {VoidPath}
                <path d={featuredPath} />
            </svg>
        </a>
    );

    const RejectedRibbon = (
        <a href="javascript:;" class="material-icons status-rejected selected" title="Featured">
            <svg xmlns="http://www.w3.org/2000/svg" height="17px" viewBox="0 0 24 24" width="17px">
                {VoidPath}
                <circle cx="15.5" cy="9.5" r="1.5" />
                <circle cx="8.5" cy="9.5" r="1.5" />
                <path d={dissatisfiedPath} />
            </svg>
        </a>
    );

    const ReplyIcon = (
        <svg
            class="comment-action-icon"
            xmlns="http://www.w3.org/2000/svg"
            width="17"
            height="17"
            viewBox="0 0 24 24"
            aria-hidden="true"
        >
            <path d={replyPath} />
            {VoidPath}
        </svg>
    );

    const ShareIcon = (
        <svg
            class="comment-action-icon"
            xmlns="http://www.w3.org/2000/svg"
            width="17"
            height="17"
            viewBox="0 0 24 24"
            aria-hidden="true"
        >
            {VoidPath}
            <path d={sharePath} />
        </svg>
    );

    const LikeIcon = (
        <svg
            class="comment-action-icon"
            xmlns="http://www.w3.org/2000/svg"
            width="17"
            height="17"
            viewBox="0 0 24 24"
            aria-hidden="true"
        >
            <path fill="none" d="M0 0h24v24H0V0z" />
            <path d={likePath} />
        </svg>
    );

    const DislikeIcon = (
        <svg
            class="comment-action-icon"
            xmlns="http://www.w3.org/2000/svg"
            width="17"
            height="17"
            viewBox="0 0 24 24"
            aria-hidden="true"
        >
            {VoidPath}
            <path d={dislikePath} />
        </svg>
    );

    const FeatureIcon = (
        <svg
            class="comment-action-icon"
            xmlns="http://www.w3.org/2000/svg"
            height="17px"
            width="17px"
            viewBox="0 0 24 24"
        >
            {VoidPath}
            <path d={favoritePath} />
        </svg>
    );

    const ApproveIcon = (
        <svg
            class="comment-action-icon"
            xmlns="http://www.w3.org/2000/svg"
            height="17px"
            width="17px"
            viewBox="0 0 24 24"
        >
            {VoidPath}
            <path d={approvePath} />
        </svg>
    );

    const CancelIcon = (
        <svg
            class="comment-action-icon"
            xmlns="http://www.w3.org/2000/svg"
            height="17px"
            width="17px"
            viewBox="0 0 24 24"
        >
            {VoidPath}
            <path d={cancelPath} />
        </svg>
    );

    const MoreIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" height="17px" viewBox="0 0 24 24" width="17px">
            {VoidPath}
            <path d={morePath} />
        </svg>
    );

    function ReviewScore({ comment }) {
        if (!comment.payload || !comment.payload.review_score) {
            return null;
        }
        return (
            <div class="talk_review">
                Review:
                <span class="input__star_rating" data-review={comment.payload.review_score}>
                    <Review score={comment.payload.review_score} />
                </span>
            </div>
        );
    }

    function AuthorReview({ comment: { author, id: commentId }, canBan, onAuthorRate }) {
        if (!canBan) {
            return null;
        }
        if (!author || !("rating" in author)) {
            return null;
        }

        const [rating, setRating] = useState(author.rating || 1);

        useEffect(() => {
            setRating(author.rating);
        }, [author.rating]);

        const onMouseEnter = useCallback((index) => {
            setRating(index);
        }, []);

        const onMouseLeave = useCallback(() => {
            setRating(author.rating || 1);
        }, [author.rating]);

        const onClick = useCallback(
            (rate) => {
                onAuthorRate(commentId, rate);
            },
            [commentId, onAuthorRate]
        );

        return (
            <span class="author-rating">
                <Review
                    enabled
                    onClick={onClick}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    score={rating}
                />
            </span>
        );
    }

    function Title({ comment }) {
        if (!comment.author) {
            return null;
        }
        return <span class="comment--item-username">{comment.author.title}</span>;
    }

    function Bio({ comment }) {
        if (!comment.author) {
            return null;
        }
        return <div class="comment--item--short-bio">{comment.author.about}</div>;
    }

    function Message(props) {
        const { comment, canBan } = props;
        const renderedComment = h("div", {
            class: "comment--item-message",
            dangerouslySetInnerHTML: { __html: comment.html },
        });
        return (
            <Fragment>
                {renderedComment}
            </Fragment>
        );
    }

    function CommentDate({ comment }) {
        const longDateFormat = formatLong(comment.created_ts);
        const shortDateFormat = formatShort(comment.created_ts);
        return (
            <span
                class="comment--item-date"
                title={`${longDateFormat}${
                    comment.is_updated ? `, last updated at ${formatLong(comment.updated_ts)}` : ""
                }`}
            >
                {shortDateFormat}
            </span>
        );
    }

    function ExtraField(fieldName) {
        return function ExtraFieldComponent({ comment: { author } }) {
            if (author && author.site_specific_data && author.site_specific_data[fieldName]) {
                const fieldNameAsClass = fieldName.replace(/_/gi, "-");
                return (
                    <div
                        class={`comment--custom-field-${fieldNameAsClass}`}
                        data-value={author.site_specific_data[fieldName]}
                    >
                        {author.site_specific_data[fieldName]}
                    </div>
                );
            } else {
                return null;
            }
        };
    }

    const AvatarBox = isLayoutActive("avatar")
        ? function AvatarBox({ comment }) {
              if (!comment.author) {
                  return null;
              }
              return (
                  <div class="comment--item-avatar">
                      <img
                          src={comment.author.image}
                          title={comment.author.title}
                          alt={comment.author.title}
                          loading="lazy"
                          width="38"
                          height="38"
                      />
                  </div>
              );
          }
        : function AvatarBox() {
              return null;
          };

    function Follow({ comment: { author } }) {
        if (!author) {
            return null;
        }
        return <div class="follow-button" data-author-id={author.id}></div>;
    }

    const layoutKeyToComponent = {
        update_author_rating: AuthorReview,
        message: Message,
        bio: Bio,
        review: ReviewScore,
        title: Title,
        date: CommentDate,
        follow: Follow,
    };

    const orderedComponents = layoutOrder
        .map((key) => {
            if (key in layoutKeyToComponent && isLayoutActive(key)) {
                return layoutKeyToComponent[key];
            } else if (key.includes("site_field_")) {
                const fieldName = key.split("site_field_")[1];
                const fieldAttribute = `site_field_${fieldName}`;
                if (isLayoutActive(fieldAttribute)) {
                    return ExtraField(fieldName);
                }
            }

            return null;
        })
        .filter(Boolean);

    function getOrderedComponents(props) {
        return orderedComponents.map((Component) => h(Component, { ...props }));
    }

    function Share({ messageId, shareUrl, encodedUrl, show, onShare }) {
        const [copiedToClipboard, setCopiedToClipboard] = useState(false);

        useEffect(() => {
            setCopiedToClipboard(false);
        }, [show]);

        const onShareClick = useCallback(() => {
            onShare();
        }, [onShare]);

        const onCopyClick = useCallback(() => {
            copyToClipboard(shareUrl);
            setCopiedToClipboard(false);
            onShare();
        }, [onShare, shareUrl]);

        return (
            <div
                class={`comment--item__tooltip_share-wrp${show ? "" : " hidden"}`}
                data-message_id={messageId}
                tabindex="-1"
            >
                <div class="comment--item__tooltip_share">
                    <a
                        onClick={onShareClick}
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}%3Fxrs%3DRebelMouse_fb%26ts%3D`}
                        data-track-share="Facebook"
                        aria-label="Share to Facebook"
                        class="share share-fb show-buttons autoclose"
                        title="Share to Facebook"
                    >
                        <i class="fa fa-facebook"></i>
                    </a>
                    <a
                        onClick={onShareClick}
                        href={`https://twitter.com/intent/tweet?url=${encodedUrl}`}
                        data-track-share="Twitter"
                        aria-label="Share to Twitter"
                        class="share share-tw show-buttons autoclose"
                        title="Share to Twitter"
                    >
                        <i class="fa fa-twitter"></i>
                    </a>
                    <a
                        onClick={onShareClick}
                        href={shareUrl}
                        data-track-share="link"
                        aria-label="Open permalink"
                        class="show-buttons autoclose"
                        target="_blank"
                        title="Open permalink"
                    >
                        <i class="fa fa-link"></i>
                    </a>
                    <a
                        onClick={onCopyClick}
                        href="javascript: void(0)"
                        data-clipboard-text={shareUrl}
                        data-track-share="copy"
                        aria-label="Copy permalink"
                        class="show-buttons copy-clipboard"
                        title="Copy permalink"
                    >
                        <i class="fa fa-paste"></i>
                    </a>
                </div>
                <div class={`comment--item__copyed-link${copiedToClipboard ? "" : " hidden"}`}>
                    copied to clipboard
                </div>
            </div>
        );
    }

    function Actions(props) {
        const { comment, shouldDisable, onReplyClick, onDislikeClick, onLikeClick } = props;

        const disabledClass = shouldDisable ? " disabled" : "";
        const [showShare, setShowShare] = useState(false);

        const onShareClick = useCallback(() => {
            setShowShare(!showShare);
        }, [showShare]);

        const onShare = useCallback(() => {
            setShowShare(false);
        }, []);
        return (
            <div>
                <a
                    onClick={onReplyClick}
                    href="javascript:;"
                    title="reply"
                    class={`comment--item__reply ${disabledClass}`}
                    data-message_id={comment.id}
                    aria-label="reply"
                >
                    {ReplyIcon}
                </a>
                <a
                    onClick={onShareClick}
                    href="javascript:;"
                    title="share"
                    class={`comment--item__share ${disabledClass}`}
                    data-message_id="${comment.id}"
                    aria-label="share"
                >
                    {ShareIcon}
                </a>
                <Share
                    messageid={comment.id}
                    shareUrl={comment.shareUrl}
                    encodedUrl={comment.encodedUrl}
                    onShare={onShare}
                    show={showShare}
                />
                <a
                    onClick={onLikeClick}
                    href="javascript:;"
                    title="like"
                    class={`comment--item__like${
                        comment.reaction === "like" ? " selected" : ""
                    }${disabledClass}`}
                    data-action="like"
                    data-message_id={comment.id}
                    aria-label="like"
                >
                    {LikeIcon}
                </a>
                <a
                    onClick={onDislikeClick}
                    href="javascript:;"
                    title="dislike"
                    class={`comment--item__dislike ${
                        comment.reaction === "dislike" ? " selected" : ""
                    }${disabledClass}`}
                    data-action="dislike"
                    data-message_id={comment.id}
                    aria-label="dislike"
                >
                    {DislikeIcon}
                </a>
            </div>
        );
    }

    function AdminActions(props) {
        const {
            comment,
            onFeatureClick,
            onApproveClick,
            onRejectClick,
            onToggleBan,
            canBan,
            canManage,
        } = props;
        return (
            <div class="admin-controls">
                {canManage && (
                    <Fragment>
                        <a
                            href="javascript:;"
                            class={`action__featured${
                                comment.status === "featured" ? " selected" : ""
                            }`}
                            data-action="featured"
                            data-message_id={comment.id}
                            onClick={onFeatureClick}
                            title="Feature"
                        >
                            {FeatureIcon}
                        </a>
                        <a
                            href="javascript:;"
                            class={`action__approved${
                                comment.status === "approved" ? " selected" : ""
                            }`}
                            data-action="approved"
                            data-message_id={comment.id}
                            onClick={onApproveClick}
                            title="Approve"
                        >
                            {ApproveIcon}
                        </a>
                        <a
                            href="javascript:;"
                            class={`action__rejected${
                                comment.status === "rejected" ? " selected" : ""
                            }`}
                            data-action="rejected"
                            data-message_id={comment.id}
                            onClick={onRejectClick}
                            title="Reject"
                        >
                            {CancelIcon}
                        </a>
                    </Fragment>
                )}
                {comment.author && canBan && (
                    <div class="comment--item--menu">
                        {MoreIcon}
                        <div class="comment--item--menu--drop-down">
                            <a href="javascript:;" onClick={onToggleBan} class="talks--menu__ban">
                                {comment.author.isBanned ? "Unban user" : "Ban user"}
                            </a>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    function Author(props) {
        const { comment, isLoggedIn, canManage, canBan, status, onReplyClick, disabled } = props;
        const [showCommentBox, setShowCommentBox] = useState(false);
        const [didPublish, setDidPublish] = useState(false);

        const _onReplyButtonClick = useCallback(
            (event) => {
                if (isLoggedIn) {
                    setShowCommentBox(!showCommentBox);
                } else {
                    onReplyClick(event);
                }
            },
            [onReplyClick, isLoggedIn, showCommentBox]
        );

        const _onSendReply = useCallback(
            (comment, replyId) => {
                onReplyClick(comment, replyId);
                setDidPublish(true);
            },
            [onReplyClick]
        );

        useEffect(() => {
            if (!disabled && didPublish) {
                setShowCommentBox(false);
                setDidPublish(false);
                return;
            }
        }, [disabled, didPublish]);

        return (
            <Fragment>
                <AvatarBox comment={comment} />
                <div class="comment--item-text">
                    {comment.status === "featured" && FeaturedRibbon}
                    {comment.status === "rejected" && RejectedRibbon}
                    {getOrderedComponents(props)}
                    <div class="comment--item--action">
                        {status !== "rejected" && (
                            <Actions {...props} onReplyClick={_onReplyButtonClick} />
                        )}
                        {(canManage || canBan) && <AdminActions {...props} />}
                    </div>
                    {!isLoggedIn && <div class="comment-login-wrp"></div>}
                </div>
                {isLoggedIn && showCommentBox && (
                    <div class="level-two">
                        <CommentBox replyId={comment.id} {...props} onReplyClick={_onSendReply} />
                    </div>
                )}
            </Fragment>
        );
    }

    function Replies(props) {
        const { comment, onShowMore, onShowReplies, isChild, canManage, canBan, settings } = props;
        const expandReplies = Boolean(settings && settings.expandReplies);

        const replyCount =
            canManage || canBan
                ? comment.counts.replies.total
                : comment.replies.length > 10
                    ? comment.counts.replies.public.total
                    : comment.replies.length;

        const [ogReplyCount, setOgReplyCount] = useState(replyCount);
        const [showReplies, setShowReplies] = useState(expandReplies && comment.replies.length > 0);
        const onShowRepliesClick = useCallback(
            (event) => {
                setShowReplies(true);
                onShowReplies(event);
            },
            [onShowReplies]
        );

        const onHideRepliesClick = useCallback(() => {
            setShowReplies(false);
        }, []);

        useEffect(() => {
            if (ogReplyCount != replyCount) {
                setShowReplies(true);
                setOgReplyCount(replyCount);
            }
        }, [ogReplyCount, replyCount]);

        if (isChild) {
            return null;
        }

        return (
            <div class={`replies level-two${replyCount ? "" : " hidden"}`}>
                <a
                    onClick={onShowRepliesClick}
                    href="javascript:;"
                    class={`comment--show--replies${showReplies ? " hidden" : ""}`}
                    data-message_id={comment.id}
                    aria-label="show replies"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                    >
                        <path d={showPath} />
                        {VoidPath}
                    </svg>
                    <span class="comment--count">{replyCount}</span>
                    {replyCount === 1 ? " Reply" : " Replies"}
                </a>
                <a
                    onClick={onHideRepliesClick}
                    href="javascript:;"
                    class={`comment--hide--replies${showReplies ? "" : " hidden"}`}
                    data-message_id={comment.id}
                    aria-label="hide replies"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                    >
                        <path d={hidePath} />
                        {VoidPath}
                    </svg>
                    Hide replies
                </a>
                <div class={`reply-items${showReplies ? "" : " hidden"}`}>
                    {comment.replies.map((reply) => (
                        <Comment
                            {...props}
                            key={reply.id}
                            comment={reply}
                            onShowMore={onShowMore}
                            isChild={true}
                        />
                    ))}
                </div>
                <div
                    onClick={onShowMore}
                    class={`talk--load_more__replies${
                        showReplies &&
                        comment.replies.length &&
                        comment.replies.length != replyCount
                            ? ""
                            : " hidden"
                    }`}
                    data-message_id={comment.id}
                >
                    Show More Replies
                </div>
            </div>
        );
    }

    function Comment(props) {
        const comment = props.comment;

        return (
            <div
                id={`comment-${comment.id}`}
                class={`comment--item ${comment.status}-comment`}
                data-talk_id={comment.talk_id}
                data-message_id={comment.id}
                data-parents={comment.parents}
            >
                <Author {...props} />
                <Replies {...props} />
            </div>
        );
    }

    return Comment;
});

export default function comment({ preact }) {
    return renderModule(preact);
}
